class BuyTogetherPopup extends HTMLElement {
  constructor() {
    super();

    this.previousActiveElement = null;
    this.openTrigger = null;
    this.closeTimeoutId = null;
  }

  connectedCallback() {
    if (!this.isConnected) return;

    const root = this.closest(".buy-together");

    if (root) {
      root.dataset.buyTogetherReady = "true";
    }

    this.moveCurrentProductFirst();
    this.setupTriggers();
    this.setupExternalTriggers();
    this.setupQueryStringTrigger();
    this.setupCloseButtons();
    this.setupAddButton();
    this.resolvePreviewProducts();
    this.resolveRemoteProducts();
    this.updateSummary();
  }

  setupTriggers() {
    const popupId = this.id;

    if (!popupId) return;

    document.querySelectorAll(`[data-buy-together-open="${popupId}"]`).forEach((trigger) => {
      trigger.addEventListener("click", () => this.open(trigger));
    });
  }

  setupQueryStringTrigger() {
    const queryParameter = this.dataset.queryParameter || "look";

    const searchParams = new URLSearchParams(window.location.search);

    if (!searchParams.has(queryParameter) || this.dataset.buyTogetherQueryOpened === "true") {
      return;
    }

    const queryValue = searchParams.get(queryParameter) || "";

    const normalizedValue = queryValue.trim().toLowerCase();

    const opensFirstAvailable = normalizedValue === "" || ["1", "true", "open"].includes(normalizedValue);

    const matchesCurrentPopup = queryValue === this.id || queryValue === this.dataset.kitId;

    if (opensFirstAvailable) {
      const firstAvailablePopup = Array.from(document.querySelectorAll("buy-together-popup[data-kit-id]")).find(
        (popup) =>
          popup.dataset.unavailable !== "true" &&
          popup.isConnected &&
          (popup.dataset.queryParameter || "look") === queryParameter,
      );

      if (firstAvailablePopup !== this) {
        return;
      }
    } else if (!matchesCurrentPopup) {
      return;
    }

    this.dataset.buyTogetherQueryOpened = "true";

    window.requestAnimationFrame(() => {
      if (!this.isConnected || this.dataset.unavailable === "true") {
        return;
      }
      const url = new URL(window.location.href);

      url.searchParams.delete(queryParameter);

      window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
      this.open(null, null);
    });
  }

  setupCloseButtons() {
    const closeButton = this.querySelector(".buy-together-popup__close");

    closeButton?.addEventListener("click", (event) => {
      event.preventDefault();
      this.close();
    });

    this.addEventListener("click", (event) => {
      const target = event.target;

      if (!(target instanceof Element)) {
        return;
      }

      const clickedBackdrop = target.closest(".buy-together-popup__backdrop");

      const clickedOutsideDialog = target === this;

      if (clickedBackdrop || clickedOutsideDialog) {
        this.close();
      }
    });

    this.addEventListener("keydown", (event) => {
      if (event.key === "Escape") {
        this.closeExternalMenus();
        this.close();
      }
    });
  } 

  setupExternalTriggers() {
    window.requestAnimationFrame(() => {
      const popups = Array.from(document.querySelectorAll("buy-together-popup[data-kit-id]")).filter(
        (popup, index, allPopups) =>
          popup.dataset.unavailable !== "true" &&
          index === allPopups.findIndex((candidate) => candidate.dataset.kitId === popup.dataset.kitId),
      );

      if (!popups.length) return;

      document.querySelectorAll("[data-buy-together-external-menu]").forEach((menu) => {
        if (menu.dataset.buyTogetherInitialized === "true") {
          return;
        }

        menu.dataset.buyTogetherInitialized = "true";

        this.setupExternalMenu(menu, popups);
      });
    });
  }

  setupExternalMenu(menu, popups) {
    const trigger = menu.querySelector("[data-buy-together-menu-trigger]");

    const options = menu.querySelector("[data-buy-together-menu-options]");

    if (!trigger || !options) return;

    menu.hidden = false;
    trigger.disabled = false;

    if (popups.length === 1) {
      const [popup] = popups;

      trigger.setAttribute("aria-controls", popup.id);
      trigger.dataset.insElementId = "buy_together_open";
      trigger.dataset.insElementPosition = "buy_together_product_mobile_gallery";
      trigger.dataset.insElementContent = popup.dataset.lookTitle || "Compre o look";
      trigger.dataset.insKitId = popup.dataset.kitId || "";

      trigger.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();

        popup.open(trigger, trigger);
      });

      return;
    }

    const currentProductId = this.closest(".buy-together")?.dataset.currentProductId || "";

    const currentProductHandle = this.closest(".buy-together")?.dataset.currentProductHandle || "";

    options.innerHTML = popups
      .map((popup) => {
        const title = popup.dataset.lookTitle || "Compre o look";

        return `
          <button
            type="button"
            class="buy-together-mobile-menu__option"
            data-buy-together-menu-option="${this.escapeHtml(popup.id)}"
            data-ins-track="click"
            data-ins-element-id="buy_together_open"
            data-ins-element-position="buy_together_product_mobile_menu"
            data-ins-content="${this.escapeHtml(title)}"
            data-ins-kit-id="${this.escapeHtml(popup.dataset.kitId || "")}"
            data-ins-product-id="${this.escapeHtml(currentProductId)}"
            data-ins-product-handle="${this.escapeHtml(currentProductHandle)}"
            data-ins-context="pdp"
          >
            <span
              class="buy-together-mobile-menu__option-hanger"
              aria-hidden="true"
            >
              <svg
                viewBox="0 0 24 24"
                width="16"
                height="16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9.5 6.5C9.5 5.12 10.62 4 12 4C13.38 4 14.5 5.12 14.5 6.5C14.5 7.52 13.89 8.4 13 8.79V10L20 15.5V17H4V15.5L11 10V8.79"
                  stroke="currentColor"
                  stroke-width="1.6"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </span>
  
            <span class="buy-together-mobile-menu__option-title">
              ${this.escapeHtml(title)}
            </span>
          </button>
        `;
      })
      .join("");

    trigger.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();

      const isOpen = trigger.getAttribute("aria-expanded") === "true";

      trigger.setAttribute("aria-expanded", String(!isOpen));

      options.hidden = isOpen;
      menu.classList.toggle("is-open", !isOpen);
    });

    options.querySelectorAll("[data-buy-together-menu-option]").forEach((option) => {
      option.addEventListener("click", () => {
        const popupId = option.dataset.buyTogetherMenuOption;

        const popup = document.getElementById(popupId);

        if (!(popup instanceof BuyTogetherPopup)) {
          return;
        }

        options.hidden = true;
        menu.classList.remove("is-open");

        trigger.setAttribute("aria-expanded", "false");

        popup.open(option, trigger);
      });
    });
  }

  moveCurrentProductFirst() {
    const root = this.closest(".buy-together");
    const currentProductId = root?.dataset.currentProductId;

    if (!root || !currentProductId) return;

    const preview = root.querySelector("[data-buy-together-preview]");

    const currentPreviewItem = preview?.querySelector(
      `[data-buy-together-preview-item][data-product-id="${currentProductId}"]`,
    );

    if (preview && currentPreviewItem) {
      preview.prepend(currentPreviewItem);
    }

    const popupItems = this.querySelector(".buy-together-popup__items");

    const currentPopupItem = popupItems?.querySelector(
      `[data-buy-together-remote-item][data-product-id="${currentProductId}"]`,
    );

    if (popupItems && currentPopupItem) {
      popupItems.prepend(currentPopupItem);
    }
  }

  async resolvePreviewProducts() {
    const root = this.closest(".buy-together");

    const previewItems = Array.from(root?.querySelectorAll("[data-buy-together-preview-item]") || []);

    if (!previewItems.length) return;

    await Promise.all(
      previewItems.map(async (item) => {
        if (item.querySelector("img")) return;

        const handleCandidates = this.getHandleCandidates(item);

        if (!handleCandidates.length) return;

        try {
          const product = await this.fetchProductByCandidates(handleCandidates);

          const selectedVariantIds = this.getSelectedVariantIds(item);

          const selectedVariant = product.variants?.find((variant) => selectedVariantIds.includes(Number(variant.id)));

          const imageUrl = this.getVariantImage(product, selectedVariant);

          const placeholder = item.querySelector(".buy-together__preview-image-placeholder");

          if (placeholder && imageUrl) {
            placeholder.outerHTML = `
              <img
                src="${this.escapeHtml(imageUrl)}"
                alt="${this.escapeHtml(product.title)}"
                loading="lazy"
                class="buy-together__preview-image"
              >
            `;
          }
        } catch (error) {
          console.error("[buy-together] Error resolving preview product", handleCandidates[0], error);
        }
      }),
    );
  }

  async resolveRemoteProducts() {
    const remoteItems = Array.from(this.querySelectorAll("[data-buy-together-remote-item]"));

    if (!remoteItems.length) {
      this.updateSummary();
      return;
    }

    await Promise.all(
      remoteItems.map(async (item) => {
        const handleCandidates = this.getHandleCandidates(item);

        if (!handleCandidates.length) {
          this.markRemoteProductAsUnresolved(
            item,
            item.dataset.originalProductHandle || "",
            "Produto sem handle configurado.",
          );

          return;
        }

        try {
          const product = await this.fetchProductByCandidates(handleCandidates);

          const selectedVariantIds = this.getSelectedVariantIds(item);

          const allowedVariants = this.getAllowedVariants(product, selectedVariantIds);

          if (!allowedVariants.length) {
            throw new Error(`No metaobject variants were found for product ${product.handle || handleCandidates[0]}`);
          }

          if (!allowedVariants.some((variant) => variant.available)) {
            this.markRemoteProductAsUnresolved(item, handleCandidates[0], "Produto indisponível.");

            return;
          }

          const selectedVariant = allowedVariants.find((variant) => variant.available) || allowedVariants[0];
          item.buyTogetherProduct = product;
          item.buyTogetherVariants = allowedVariants;
          item.classList.remove("is-loading");
          item.classList.remove("is-disabled");
          item.dataset.selected = "true";
          item.dataset.variantId = String(selectedVariant.id);
          item.dataset.productId = String(product.id);
          item.setAttribute("data-buy-together-item", "");
          item.innerHTML = this.buildRemoteProductContent(product, selectedVariant, allowedVariants);
          this.bindRemoteItemEvents(item);
          this.syncVariantInputs(item, product, selectedVariant);
          this.refreshVariantInputAvailability(item);
        } catch (error) {
          console.error("[buy-together] Error resolving product", handleCandidates[0], error);

          this.markRemoteProductAsUnresolved(
            item,
            handleCandidates[0],
            "Nenhuma variante cadastrada no metaobjeto foi encontrada.",
          );
        }
      }),
    );

    this.updateSummary();
  }

  getHandleCandidates(item) {
    const rawCandidates = item.dataset.productHandleCandidates || item.dataset.productHandle || "";

    return Array.from(
      new Set(
        rawCandidates
          .split("|")
          .map((handle) => handle.trim().replace(/^\/+|\/+$/g, ""))
          .filter((handle) => handle && !handle.includes("..") && !handle.includes("?") && !handle.includes("#")),
      ),
    );
  }

  async fetchProductByCandidates(handleCandidates) {
    let lastError;

    for (const handle of handleCandidates) {
      try {
        const response = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
          headers: {
            Accept: "application/json",
          },
        });

        if (!response.ok) {
          lastError = new Error(`Unable to fetch product ${handle}`);

          continue;
        }

        return response.json();
      } catch (error) {
        lastError = error;
      }
    }

    throw lastError || new Error("Unable to fetch product");
  }

  getSelectedVariantIds(item) {
    const value = item.dataset.selectedVariantIds || "";

    return value
      .split(",")
      .map((variantId) => Number(variantId.trim()))
      .filter(Boolean);
  }

  getAllowedVariants(product, selectedVariantIds) {
    if (!selectedVariantIds.length) {
      return [];
    }

    return product.variants.filter((variant) => selectedVariantIds.includes(Number(variant.id)));
  }

  bindRemoteItemEvents(item) {
    item.querySelectorAll("[data-buy-together-variant-input]").forEach((input) => {
      input.addEventListener("change", (event) => {
        event.stopPropagation();

        this.handleVariantInputChange(item, input);
      });

      input.addEventListener("click", (event) => {
        event.stopPropagation();
      });
    });
  }

  buildRemoteProductContent(product, selectedVariant, allowedVariants) {
    const imageUrl = this.getVariantImage(product, selectedVariant);
    const productUrl = product.url || `/products/${product.handle}`;

    const discountPercentage = Number(this.dataset.discountPercentage || 0);

    const originalPrice = Number(selectedVariant.price || 0);

    const discountedPrice =
      discountPercentage > 0 ? Math.round(originalPrice * (1 - discountPercentage / 100)) : originalPrice;

    const formattedOriginalPrice = this.formatMoney(originalPrice);

    const formattedDiscountedPrice = this.formatMoney(discountedPrice);

    const productOptions = Array.isArray(product.options) ? product.options : [];

    const colorOptionIndex = productOptions.findIndex((option) => this.isColorOption(this.getOptionName(option)));

    const sizeOptionIndex = productOptions.findIndex((option) => this.isSizeOption(this.getOptionName(option)));

    const modelOptionIndex = productOptions.findIndex((option) => this.isModelOption(this.getOptionName(option)));

    const colorValues = this.getUniqueOptionValues(allowedVariants, colorOptionIndex);

    const sizeValues = this.sortSizeValues(this.getUniqueOptionValues(allowedVariants, sizeOptionIndex));

    const modelValues = this.getUniqueOptionValues(allowedVariants, modelOptionIndex);

    const hasColorSelector = colorOptionIndex >= 0 && colorValues.length > 1;

    const hasSizeSelector = sizeOptionIndex >= 0 && sizeValues.length > 1;

    const hasModelSelector = modelOptionIndex >= 0 && modelValues.length > 1;

    const visibleVariantGroupCount = [hasColorSelector, hasSizeSelector, hasModelSelector].filter(Boolean).length;

    const hideVariantGroupLabel = visibleVariantGroupCount === 1;

    const imageMarkup = imageUrl
      ? `
        <a
          href="${this.escapeHtml(productUrl)}"
          class="buy-together-popup__item-image-link"
          aria-label="Ver ${this.escapeHtml(product.title)}"
        >
          <img
            src="${this.escapeHtml(imageUrl)}"
            alt="${this.escapeHtml(product.title)}"
            loading="lazy"
            class="buy-together-popup__item-image"
            data-buy-together-item-image
          >
        </a>
      `
      : "";

    const colorMarkup = hasColorSelector
      ? this.buildVariantRadioGroupMarkup({
          label: "Cor",
          type: "color",
          values: colorValues,
          hideLabel: hideVariantGroupLabel,
          selectedValue: this.getVariantOptionValue(selectedVariant, colorOptionIndex),
        })
      : "";

    const sizeMarkup = hasSizeSelector
      ? this.buildVariantRadioGroupMarkup({
          label: "Tamanho",
          type: "size",
          values: sizeValues,
          hideLabel: hideVariantGroupLabel,
          selectedValue: this.getVariantOptionValue(selectedVariant, sizeOptionIndex),
        })
      : "";

    const modelMarkup = hasModelSelector
      ? this.buildVariantRadioGroupMarkup({
          label: "Modelo",
          type: "model",
          values: modelValues,
          hideLabel: hideVariantGroupLabel,
          selectedValue: this.getVariantOptionValue(selectedVariant, modelOptionIndex),
        })
      : "";

    const fixedColorMarkup =
      colorOptionIndex >= 0 && colorValues.length === 1
        ? `
          <span class="buy-together-popup__item-variant">
            Cor: ${this.escapeHtml(colorValues[0])}
          </span>
        `
        : "";

    const fixedSizeMarkup =
      sizeOptionIndex >= 0 && sizeValues.length === 1
        ? `
          <span class="buy-together-popup__item-variant">
            Tamanho: ${this.escapeHtml(sizeValues[0])}
          </span>
        `
        : "";

    const fixedModelMarkup =
      modelOptionIndex >= 0 && modelValues.length === 1
        ? `
        <span class="buy-together-popup__item-variant">
          Modelo: ${this.escapeHtml(modelValues[0])}
        </span>
      `
        : "";

    const variantFieldsMarkup =
      colorMarkup || modelMarkup || sizeMarkup
        ? `
          <div class="buy-together-popup__variant-fields">
            ${colorMarkup}
            ${sizeMarkup}
            ${modelMarkup}
          </div>
        `
        : `
          ${fixedColorMarkup}
          ${fixedSizeMarkup}
          ${fixedModelMarkup}
        `;

    return `
      ${imageMarkup}

      <div class="buy-together-popup__item-content">
        <span class="buy-together-popup__item-title">
          ${this.escapeHtml(product.title)}
        </span>

        <span
          class="buy-together-popup__item-price"
          data-buy-together-item-price
        >
          ${
            discountPercentage > 0
              ? `
                <span class="buy-together-popup__item-price-original">
                  ${formattedOriginalPrice}
                </span>

                <span class="buy-together-popup__item-price-discounted">
                  ${formattedDiscountedPrice}
                </span>
              `
              : formattedOriginalPrice
          }
        </span>

        ${variantFieldsMarkup}
      </div>
    `;
  }

  buildVariantRadioGroupMarkup({ label, type, values, selectedValue, hideLabel = false }) {
    const groupName = `buy-together-${type}-${Math.random().toString(36).slice(2)}`;

    const isColorGroup = type === "color";

    return `
      <fieldset class="buy-together-popup__variant-group buy-together-popup__variant-group--${this.escapeHtml(type)}">
        <legend
          class="buy-together-popup__variant-group-label${hideLabel ? " visually-hidden" : ""}"
        >
          ${this.escapeHtml(label)}
        </legend>

        <div class="buy-together-popup__variant-options">
          ${values
            .map((value) => {
              const normalizedValue = this.handleize(value);
              const inputId = `${groupName}-${normalizedValue}`;
              const checked = value === selectedValue ? "checked" : "";

              if (isColorGroup) {
                return `
                  <div
                    class="buy-together-popup__variant-option buy-together-popup__variant-option--color variant-option-space"
                  >
                    <input
                      id="${this.escapeHtml(inputId)}"
                      type="radio"
                      name="${this.escapeHtml(groupName)}"
                      value="${this.escapeHtml(value)}"
                      aria-label="${this.escapeHtml(value)}"
                      data-buy-together-variant-input="color"
                      ${checked}
                    >

                    <label
                      class="buy-together-popup__color-swatch color-swatch background--${this.escapeHtml(
                        normalizedValue,
                      )}"
                      for="${this.escapeHtml(inputId)}"
                      data-color-handle="${this.escapeHtml(normalizedValue)}"
                      title="${this.escapeHtml(value)}"
                    >
                      <span class="visually-hidden">
                        ${this.escapeHtml(value)}
                      </span>
                    </label>
                  </div>
                `;
              }

              return `
                <div
                  class="buy-together-popup__variant-option buy-together-popup__variant-option--${this.escapeHtml(
                    type,
                  )} variant-option-space"
                >
                  <input
                    id="${this.escapeHtml(inputId)}"
                    type="radio"
                    name="${this.escapeHtml(groupName)}"
                    value="${this.escapeHtml(value)}"
                    data-buy-together-variant-input="${this.escapeHtml(type)}"
                    ${checked}
                  >

                  <label
                    class="buy-together-popup__variant-option-label buy-together-popup__variant-option-label--${this.escapeHtml(
                      type,
                    )}"
                    for="${this.escapeHtml(inputId)}"
                  >
                    ${this.escapeHtml(value)}
                  </label>
                </div>
              `;
            })
            .join("")}
        </div>
      </fieldset>
    `;
  }

  handleize(value) {
    return String(value ?? "")
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  getOptionIndexes(product) {
    const productOptions = Array.isArray(product.options) ? product.options : [];

    return {
      color: productOptions.findIndex((option) => this.isColorOption(this.getOptionName(option))),

      size: productOptions.findIndex((option) => this.isSizeOption(this.getOptionName(option))),

      model: productOptions.findIndex((option) => this.isModelOption(this.getOptionName(option))),
    };
  }

  getSelectedOptionValues(item) {
    return {
      color: item.querySelector('[data-buy-together-variant-input="color"]:checked')?.value,

      size: item.querySelector('[data-buy-together-variant-input="size"]:checked')?.value,

      model: item.querySelector('[data-buy-together-variant-input="model"]:checked')?.value,
    };
  }

  variantMatchesSelection(variant, optionIndexes, selectedValues) {
    return ["color", "size", "model"].every((type) => {
      const optionIndex = optionIndexes[type];

      const selectedValue = selectedValues[type];

      return !selectedValue || optionIndex < 0 || this.getVariantOptionValue(variant, optionIndex) === selectedValue;
    });
  }

  syncVariantInputs(item, product, variant) {
    const optionIndexes = this.getOptionIndexes(product);

    Object.entries(optionIndexes).forEach(([type, optionIndex]) => {
      if (optionIndex < 0) return;

      const selectedValue = this.getVariantOptionValue(variant, optionIndex);

      const input = Array.from(item.querySelectorAll(`[data-buy-together-variant-input="${type}"]`)).find(
        (candidate) => candidate.value === selectedValue,
      );

      if (input) {
        input.checked = true;
      }
    });
  }

  updateItemVariantContent(item, product, variant) {
    const priceElement = item.querySelector("[data-buy-together-item-price]");

    if (priceElement) {
      const discountPercentage = Number(this.dataset.discountPercentage || 0);

      const originalPrice = Number(variant.price || 0);

      const discountedPrice =
        discountPercentage > 0 ? Math.round(originalPrice * (1 - discountPercentage / 100)) : originalPrice;

      priceElement.innerHTML =
        discountPercentage > 0
          ? `
          <span class="buy-together-popup__item-price-original">
            ${this.formatMoney(originalPrice)}
          </span>

          <span class="buy-together-popup__item-price-discounted">
            ${this.formatMoney(discountedPrice)}
          </span>
        `
          : this.formatMoney(originalPrice);
    }

    const imageElement = item.querySelector("[data-buy-together-item-image]");

    const imageUrl = this.getVariantImage(product, variant);

    if (imageElement && imageUrl) {
      imageElement.src = imageUrl;
    }
  }

  handleVariantInputChange(item, changedInput) {
    const product = item.buyTogetherProduct;

    const variants = item.buyTogetherVariants || [];

    if (!product || !variants.length) {
      return;
    }

    const optionIndexes = this.getOptionIndexes(product);

    const selectedValues = this.getSelectedOptionValues(item);

    const changedType = changedInput.dataset.buyTogetherVariantInput;

    const changedValue = changedInput.value;

    let matchingVariant = variants.find(
      (variant) => variant.available && this.variantMatchesSelection(variant, optionIndexes, selectedValues),
    );

    if (!matchingVariant) {
      matchingVariant = this.findBestAvailableVariant(
        variants,
        optionIndexes,
        selectedValues,
        changedType,
        changedValue,
      );
    }

    if (!matchingVariant) {
      delete item.dataset.variantId;

      item.dataset.selected = "false";
      item.classList.add("is-disabled");

      this.refreshVariantInputAvailability(item);

      this.updateSummary();

      return;
    }

    item.dataset.variantId = String(matchingVariant.id);

    item.dataset.selected = "true";
    item.classList.remove("is-disabled");

    this.syncVariantInputs(item, product, matchingVariant);

    this.refreshVariantInputAvailability(item);

    this.updateItemVariantContent(item, product, matchingVariant);

    this.updateSummary();
  }

  refreshVariantInputAvailability(item) {
    const product = item.buyTogetherProduct;

    const variants = item.buyTogetherVariants || [];

    if (!product || !variants.length) {
      return;
    }

    const optionIndexes = this.getOptionIndexes(product);

    const selectedValues = this.getSelectedOptionValues(item);

    ["color", "size", "model"].forEach((type) => {
      if (optionIndexes[type] < 0) {
        return;
      }

      item.querySelectorAll(`[data-buy-together-variant-input="${type}"]`).forEach((input) => {
        const existsInConfiguredVariants = variants.some((variant) =>
          this.variantMatchesOptionContext(variant, optionIndexes, selectedValues, type, input.value),
        );

        const hasAvailableVariant = variants.some(
          (variant) =>
            variant.available &&
            this.variantMatchesOptionContext(variant, optionIndexes, selectedValues, type, input.value),
        );

        const optionWrapper = input.closest(".buy-together-popup__variant-option");

        /*
         * O SCSS atual já estiliza os
         * inputs desabilitados.
         */
        input.disabled = !hasAvailableVariant;

        input.setAttribute("aria-disabled", String(!hasAvailableVariant));

        /*
         * Valores que não pertencem à
         * combinação atual são ocultados.
         *
         * Valores cadastrados, mas sem
         * estoque, permanecem visíveis
         * e desabilitados.
         */
        if (optionWrapper) {
          optionWrapper.hidden = !existsInConfiguredVariants;
        }
      });
    });
  }

  findBestAvailableVariant(variants, optionIndexes, selectedValues, changedType, changedValue) {
    const changedOptionIndex = optionIndexes[changedType];

    if (changedOptionIndex < 0) {
      return null;
    }

    const candidates = variants.filter(
      (variant) => variant.available && this.getVariantOptionValue(variant, changedOptionIndex) === changedValue,
    );

    if (!candidates.length) {
      return null;
    }

    const orderedTypes = ["color", "size", "model"].filter((type) => type !== changedType);

    return candidates
      .map((variant) => {
        const score = orderedTypes.reduce((total, type) => {
          const optionIndex = optionIndexes[type];

          const selectedValue = selectedValues[type];

          if (optionIndex < 0 || !selectedValue) {
            return total;
          }

          return this.getVariantOptionValue(variant, optionIndex) === selectedValue ? total + 1 : total;
        }, 0);

        return {
          variant,
          score,
        };
      })
      .sort((first, second) => second.score - first.score)[0].variant;
  }

  getOptionName(option) {
    if (typeof option === "string") {
      return option;
    }

    return option?.name || option?.title || "";
  }

  getVariantOptionValue(variant, optionIndex) {
    if (optionIndex < 0) return "";

    const indexedOption = variant[`option${optionIndex + 1}`];

    if (indexedOption) {
      return indexedOption;
    }

    if (Array.isArray(variant.options)) {
      return variant.options[optionIndex] || "";
    }

    return "";
  }

  isColorOption(name) {
    return ["cor", "color", "colour"].includes(
      String(name || "")
        .trim()
        .toLowerCase(),
    );
  }

  isSizeOption(name) {
    return ["tamanho", "size"].includes(
      String(name || "")
        .trim()
        .toLowerCase(),
    );
  }

  isModelOption(name) {
    return ["modelo", "model"].includes(
      String(name || "")
        .trim()
        .toLowerCase(),
    );
  }

  getUniqueOptionValues(variants, optionIndex) {
    if (optionIndex < 0) return [];

    return Array.from(
      new Set(
        variants
          .map((variant) => this.getVariantOptionValue(variant, optionIndex))
          .filter((value) => value && value !== "Default Title"),
      ),
    );
  }

  getConfiguredSizeOrder() {
    const rawOrder = this.dataset.sizeOrder || "";

    if (!rawOrder.trim()) {
      return [];
    }

    return rawOrder
      .split(/[,;\n|]+/)
      .map((value) => value.trim())
      .filter(Boolean);
  }

  normalizeVariantOptionValue(value) {
    return String(value || "")
      .trim()
      .toLocaleLowerCase("pt-BR");
  }

  sortSizeValues(values) {
    const configuredOrder = this.getConfiguredSizeOrder();

    if (!configuredOrder.length) {
      return values;
    }

    const orderMap = new Map(configuredOrder.map((value, index) => [this.normalizeVariantOptionValue(value), index]));

    return [...values].sort((firstValue, secondValue) => {
      const firstIndex = orderMap.get(this.normalizeVariantOptionValue(firstValue));

      const secondIndex = orderMap.get(this.normalizeVariantOptionValue(secondValue));

      const firstIsConfigured = firstIndex !== undefined;

      const secondIsConfigured = secondIndex !== undefined;

      if (firstIsConfigured && secondIsConfigured) {
        return firstIndex - secondIndex;
      }

      if (firstIsConfigured) {
        return -1;
      }

      if (secondIsConfigured) {
        return 1;
      }

      /*
       * Valores não previstos no setting
       * preservam a ordem original.
       */
      return 0;
    });
  }

  getVariantImage(product, variant) {
    return (
      variant?.featured_image?.src || variant?.featured_image || product.featured_image || product.images?.[0] || ""
    );
  }

  variantMatchesOptionContext(variant, optionIndexes, selectedValues, type, candidateValue) {
    const optionIndex = optionIndexes[type];

    if (optionIndex < 0 || this.getVariantOptionValue(variant, optionIndex) !== candidateValue) {
      return false;
    }

    if (type === "color") {
      return true;
    }

    const selectedColor = selectedValues.color;
    const colorOptionIndex = optionIndexes.color;

    if (
      selectedColor &&
      colorOptionIndex >= 0 &&
      this.getVariantOptionValue(variant, colorOptionIndex) !== selectedColor
    ) {
      return false;
    }

    if (type === "size") {
      return true;
    }

    const selectedSize = selectedValues.size;
    const sizeOptionIndex = optionIndexes.size;

    if (selectedSize && sizeOptionIndex >= 0 && this.getVariantOptionValue(variant, sizeOptionIndex) !== selectedSize) {
      return false;
    }

    return true;
  }

  markRemoteProductAsUnresolved(item, handle, message) {
    this.dataset.unavailable = "true";

    const root = this.closest(".buy-together");

    if (root) {
      root.hidden = true;
    }

    document
      .querySelectorAll(`[data-buy-together-menu-option="${CSS.escape(this.id)}"]`)
      .forEach((option) => option.remove());

    document.querySelectorAll("[data-buy-together-external-menu]").forEach((menu) => {
      const remainingOptions = menu.querySelectorAll("[data-buy-together-menu-option]");

      if (!remainingOptions.length) {
        menu.hidden = true;
      }
    });
    item.classList.remove("is-loading");
    item.classList.add("is-disabled");

    item.dataset.selected = "false";

    item.removeAttribute("data-buy-together-item");

    delete item.dataset.variantId;

    item.innerHTML = `
      <div class="buy-together-popup__item-content">
        <span class="buy-together-popup__item-title">
          ${this.escapeHtml(handle || item.dataset.originalProductHandle || "Produto do look")}
        </span>

        <span class="buy-together-popup__item-unavailable">
          ${this.escapeHtml(message || "Produto indisponível.")}
        </span>
      </div>
    `;
  }

  formatPropertyMoney(valueInCents) {
    return (Number(valueInCents || 0) / 100).toFixed(2);
  }

  formatMoney(valueInCents) {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(Number(valueInCents || 0) / 100);
  }

  escapeHtml(value) {
    return String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }
  getErrorElement() {
    let errorElement = this.querySelector("[data-buy-together-error]");

    if (errorElement) {
      return errorElement;
    }

    const footer = this.querySelector(".buy-together-popup__footer");

    const addButton = this.querySelector("[data-buy-together-add]");

    if (!footer || !addButton) {
      return null;
    }

    errorElement = document.createElement("p");

    errorElement.className = "buy-together-popup__error";

    errorElement.setAttribute("data-buy-together-error", "");

    errorElement.setAttribute("role", "alert");

    errorElement.hidden = true;

    footer.insertBefore(errorElement, addButton);

    return errorElement;
  }

  clearError() {
    const errorElement = this.getErrorElement();

    if (!errorElement) return;

    errorElement.textContent = "";
    errorElement.hidden = true;
  }

  showError(message) {
    const errorElement = this.getErrorElement();

    if (!errorElement) return;

    errorElement.textContent = message || "Não foi possível adicionar o look ao carrinho.";

    errorElement.hidden = false;
  }

  setupAddButton() {
    const addButton = this.querySelector("[data-buy-together-add]");

    if (!addButton) return;

    addButton.addEventListener("click", async () => {
      this.clearError();

      const selectedItems = this.getSelectedItems();

      const expectedItemCount = this.getExpectedItemCount();

      const hasCompleteLook = expectedItemCount > 0 && selectedItems.length === expectedItemCount;

      if (!hasCompleteLook) {
        this.updateSummary();
        return;
      }

      const kitId = this.dataset.kitId || "";

      if (!kitId) {
        console.error("[buy-together] Missing Cupinn kit id");

        return;
      }

      const cupinnProductSource = this.dataset.productSource || "buy-together";

      const cartUi = document.querySelector("cart-drawer") || document.querySelector("cart-notification");

      const kitCompareAtPrice = selectedItems.reduce((total, item) => {
        const variant = item.buyTogetherVariants?.find(
          (candidate) => Number(candidate.id) === Number(item.dataset.variantId),
        );

        const compareAtPrice = Number(variant?.compare_at_price) || Number(variant?.price) || 0;

        return total + compareAtPrice;
      }, 0);

      const requestBody = {
        items: selectedItems.map((item) => {
          const variant = item.buyTogetherVariants?.find(
            (candidate) => Number(candidate.id) === Number(item.dataset.variantId),
          );

          const compareAtPrice = Number(variant?.compare_at_price) || Number(variant?.price) || 0;

          return {
            id: Number(item.dataset.variantId),
            quantity: 1,
            properties: {
              _compare_at_price: this.formatPropertyMoney(compareAtPrice),
              _kit_compare_at_price: this.formatPropertyMoney(kitCompareAtPrice),
              __kitId: kitId,
              __productSource: cupinnProductSource,
            },
          };
        }),
        sections: "cart-drawer,cart-icon-bubble",
      };

      addButton.disabled = true;

      addButton.classList.add("is-loading");

      try {
        const response = await fetch("/cart/add.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "site-placement": cupinnProductSource,
          },
          body: JSON.stringify(requestBody),
        });

        const payload = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(payload.description || payload.message || "Não foi possível adicionar o look ao carrinho.");
        }

        let cartPayload = payload;

        if (cartUi && typeof cartUi.getSectionsToRender === "function") {
          const sectionIds = cartUi
            .getSectionsToRender()
            .map((section) => section.id)
            .filter(Boolean);

          if (sectionIds.length) {
            const rootRoute = window.Shopify?.routes?.root || "/";

            const sectionsResponse = await fetch(`${rootRoute}?sections=${sectionIds.join(",")}`, {
              headers: {
                Accept: "application/json",
              },
            });

            if (sectionsResponse.ok) {
              const sections = await sectionsResponse.json();

              cartPayload = {
                ...payload,
                sections,
              };
            }
          }
        }

        if (cartUi && typeof cartUi.renderContents === "function" && cartPayload.sections) {
          if (window.CartFeedback) window.CartFeedback.present(cartUi, cartPayload, "buy-together");
              else cartUi.renderContents(cartPayload);
        } else {
          document.dispatchEvent(
            new CustomEvent("cart:updated", {
              bubbles: true,
              detail: {
                source: "buy-together",
                cart: cartPayload,
              },
            }),
          );
        }

        this.close();
      } catch (error) {
        console.error("[buy-together] Error adding Cupinn look to cart", error);

        this.showError(error instanceof Error ? error.message : "Não foi possível adicionar o look ao carrinho.");
      } finally {
        addButton.classList.remove("is-loading");

        this.updateSummary();
      }
    });
  }

  open(trigger, focusReturnElement = trigger) {
    if (this.closeTimeoutId) {
      window.clearTimeout(this.closeTimeoutId);
      this.closeTimeoutId = null;
    }

    this.previousActiveElement =
      focusReturnElement instanceof HTMLElement ? focusReturnElement : document.activeElement;

    this.openTrigger = trigger instanceof HTMLElement ? trigger : null;

    this.hidden = false;

    document.documentElement.classList.add("buy-together-popup-is-open");

    if (this.openTrigger?.hasAttribute("aria-expanded")) {
      this.openTrigger.setAttribute("aria-expanded", "true");
    }

    window.requestAnimationFrame(() => {
      this.classList.add("is-open");

      const dialog = this.querySelector(".buy-together-popup__dialog");

      const closeButton = this.querySelector(".buy-together-popup__close");

      if (!(dialog instanceof HTMLElement)) {
        return;
      }

      const initialFocus =
        closeButton instanceof HTMLElement && closeButton.offsetParent !== null ? closeButton : dialog;

      trapFocus(dialog, initialFocus);
    });
  }

  closeExternalMenus() {
    document.querySelectorAll("[data-buy-together-external-menu]").forEach((menu) => {
      const trigger = menu.querySelector("[data-buy-together-menu-trigger]");

      const options = menu.querySelector("[data-buy-together-menu-options]");

      menu.classList.remove("is-open");

      if (trigger) {
        trigger.setAttribute("aria-expanded", "false");
      }

      if (options) {
        options.hidden = true;
      }
    });
  }

  close() {
    this.classList.remove("is-open");

    removeTrapFocus();

    document.documentElement.classList.remove("buy-together-popup-is-open");

    if (this.openTrigger?.hasAttribute("aria-expanded")) {
      this.openTrigger.setAttribute("aria-expanded", "false");
    }

    this.openTrigger = null;

    document.querySelectorAll(`[data-buy-together-open="${this.id}"]`).forEach((trigger) => {
      trigger.setAttribute("aria-expanded", "false");
    });

    if (this.closeTimeoutId) {
      window.clearTimeout(this.closeTimeoutId);
    }

    this.closeTimeoutId = window.setTimeout(() => {
      this.hidden = true;
      this.closeTimeoutId = null;

      if (this.previousActiveElement instanceof HTMLElement && this.previousActiveElement.isConnected) {
        this.previousActiveElement.focus();
      }

      this.previousActiveElement = null;
    }, 240);
  }

  getSelectedItems() {
    return Array.from(this.querySelectorAll('[data-buy-together-item][data-selected="true"]')).filter(
      (item) => item.dataset.variantId,
    );
  }

  getExpectedItemCount() {
    return Number(this.dataset.expectedItemCount || 0);
  }

  updateSummary() {
    const selectedCount = this.getSelectedItems().length;
    const expectedItemCount = this.getExpectedItemCount();
    const addButton = this.querySelector("[data-buy-together-add]");

    if (!addButton) return;

    const hasCompleteLook = expectedItemCount > 0 && selectedCount === expectedItemCount;

    addButton.disabled = !hasCompleteLook;

    addButton.innerHTML = hasCompleteLook
      ? `Adicionar ${expectedItemCount} peças ao carrinho`
      : `Selecione todos os ${expectedItemCount} itens`;
  }
}

if (!customElements.get("buy-together-popup")) {
  customElements.define("buy-together-popup", BuyTogetherPopup);
}
